import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSignedDocumentErrorText } from '../../hooks/use-signed-document-error-text'
import {
    SignedDocument,
    SignedDocumentMail,
    SignedDocumentType,
} from '../../types'
import {
    formatActorName,
    formatDocumentTime,
} from '../../utils/signed-document'

dayjs.extend(utc)

interface TimelineEvent {
    key: string
    time: string
    content: ReactNode
}

interface SignedDocumentTimelineProps {
    documentType: SignedDocumentType
    /** Bản mới nhất (GET trạng thái) — có thể `cancelled` khi ký lại chưa tạo được bản mới */
    latest: SignedDocument
    /** Mail gửi khách (mọi bản) */
    mails?: SignedDocumentMail[]
}

const Tag = ({
    tone,
    children,
}: {
    tone: 'gray' | 'green' | 'red'
    children: ReactNode
}) => {
    const styles = {
        gray: 'bg-gray-100 text-gray-600',
        green: 'bg-green-50 text-green-700',
        red: 'bg-red-50 text-red-700',
    }
    return (
        <span className={`rounded px-1.5 py-0.5 text-xs ${styles[tone]}`}>
            {children}
        </span>
    )
}

const LinkButton = ({
    onClick,
    children,
}: {
    onClick: () => void
    children: ReactNode
}) => (
    <button
        type="button"
        className="text-primary hover:underline"
        onClick={onClick}
    >
        {children}
    </button>
)

/**
 * Lịch sử thao tác của 1 chứng từ: timeline gộp, mới nhất trước — mỗi phiên bản (bản mới nhất +
 * `history[]`): gửi yêu cầu ký, đã ký, (bản đã huỷ) huỷ để ký lại + Xem / Tải file cũ; mỗi mail
 * gửi khách. Tên người thao tác lấy sẵn từ API (`*_name`), rỗng → "—". Thu gọn mặc định.
 */
const SignedDocumentTimeline = ({
    documentType,
    latest,
    mails = [],
}: SignedDocumentTimelineProps) => {
    const { t } = useTranslation('invoice')
    const errorText = useSignedDocumentErrorText(documentType)
    const [expanded, setExpanded] = useState(false)

    const events = useMemo(() => {
        const versions = [latest, ...(latest.history ?? [])]
        // Bản hiện tại = bản mới nhất chưa huỷ; mail đính kèm bản khác → "bản cũ"
        const currentID =
            latest.status === 'cancelled' ? undefined : latest.document_id
        const list: TimelineEvent[] = []

        versions.forEach((version) => {
            const id = version.document_id
            const requestedAt = version.requested_at ?? version.created_at
            if (requestedAt) {
                list.push({
                    key: `requested-${id}`,
                    time: requestedAt,
                    content: t('signed-document-timeline-requested', {
                        name: formatActorName(version.requested_by_name),
                    }),
                })
            }
            if (version.signed_at) {
                list.push({
                    key: `signed-${id}`,
                    time: version.signed_at,
                    content: t('signed-document-timeline-signed'),
                })
            }
            if (version.status === 'cancelled' && version.cancelled_at) {
                list.push({
                    key: `cancelled-${id}`,
                    time: version.cancelled_at,
                    content: (
                        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                            <Tag tone="gray">
                                {t('signed-document-status-cancelled')}
                            </Tag>
                            <span>
                                {t('signed-document-timeline-cancelled', {
                                    name: formatActorName(
                                        version.cancelled_by_name
                                    ),
                                })}
                                {version.cancel_reason &&
                                    ` · ${t('signed-document-timeline-reason', {
                                        reason: version.cancel_reason,
                                    })}`}
                            </span>
                            {version.file_url && (
                                <LinkButton
                                    onClick={() =>
                                        window.open(
                                            version.file_url,
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }
                                >
                                    {t('signed-document-timeline-view')}
                                </LinkButton>
                            )}
                            {version.file_download_url && (
                                <LinkButton
                                    onClick={() => {
                                        // Tên file do backend đặt, vd bien-ban-doi-soat-HD-<id>-da-huy-<id bản>.pdf
                                        if (version.file_download_url)
                                            window.location.assign(
                                                version.file_download_url
                                            )
                                    }}
                                >
                                    {t('signed-document-timeline-download')}
                                </LinkButton>
                            )}
                        </span>
                    ),
                })
            }
        })

        mails.forEach((mail) => {
            const failed = mail.status !== 'sent'
            list.push({
                key: `mail-${mail.mail_id}`,
                time: mail.created_at,
                content: (
                    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>
                            {t('signed-document-timeline-mail', {
                                name: formatActorName(mail.sent_by_name),
                                to: mail.to.join(', '),
                            })}
                        </span>
                        <span
                            title={
                                failed && mail.error_message
                                    ? errorText(mail.error_message, {
                                          fallback: 'esign-mail-send-failed',
                                      })
                                    : undefined
                            }
                        >
                            <Tag tone={failed ? 'red' : 'green'}>
                                {t(
                                    failed
                                        ? 'signed-document-mail-status-failed'
                                        : 'signed-document-mail-status-sent'
                                )}
                            </Tag>
                        </span>
                        {mail.payment_request_id !== currentID && (
                            <Tag tone="gray">
                                {t('signed-document-mail-old-version')}
                            </Tag>
                        )}
                    </span>
                ),
            })
        })

        // Mới nhất trước; so theo UTC (API có thể trả thiếu `Z`)
        return list.sort(
            (a, b) => dayjs.utc(b.time).valueOf() - dayjs.utc(a.time).valueOf()
        )
    }, [latest, mails, t, errorText])

    if (!events.length) return null

    return (
        <div className="mb-3 rounded-md border border-gray-200 text-sm">
            <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-2 font-medium text-gray-700"
                onClick={() => setExpanded((value) => !value)}
            >
                {t('signed-document-timeline', { total: events.length })}
                {expanded ? (
                    <ChevronUp className="h-4 w-4" />
                ) : (
                    <ChevronDown className="h-4 w-4" />
                )}
            </button>
            {expanded && (
                <ol className="space-y-2 border-t border-gray-200 px-4 py-3">
                    {events.map((event) => (
                        <li key={event.key} className="flex gap-3">
                            <span className="w-[120px] shrink-0 whitespace-nowrap text-gray-500">
                                {formatDocumentTime(event.time)}
                            </span>
                            <span className="text-gray-800">
                                {event.content}
                            </span>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    )
}

export default SignedDocumentTimeline
